import { useEffect, useMemo, useState } from "react";
import { IconChevronLeft, IconChevronRight, IconRefresh } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Alert, Card, Group, Heading, IconAction, PageHeader, Select, Skeleton, Stack, Text } from "@/components/ui";
import { cn } from "@/lib/cn";
import { useFiltrosHistorico } from "@/features/historico-documental/hooks/use-historico";
import { calendarioPagosQueryOptions, useCalendarioPagos } from "../hooks/use-calendario-pagos";
import type { PagoProgramado } from "../services/calendario-pagos.api";
import { CalendarioMensual } from "./CalendarioMensual";
import { PagosDelDiaPanel } from "./PagosDelDiaPanel";
import { PagosTable } from "./PagosTable";
import { ReprogramarFechaModal } from "./ReprogramarFechaModal";
import { SemaforoLeyenda } from "./SemaforoPago";

const FMT = new Intl.NumberFormat("es-MX", { minimumFractionDigits: 2 });

const PAGE_SIZE_TABLA = 15;

/** Toggle Calendario/Tabla · pill bespoke (sin átomo `SegmentedControl` compartido). */
function VistaToggle({ vista, onChange }: { vista: "calendario" | "tabla"; onChange: (v: "calendario" | "tabla") => void }) {
  const opciones = [
    { value: "calendario" as const, label: "Calendario" },
    { value: "tabla" as const, label: "Tabla" },
  ];
  return (
    <div className="inline-flex items-center gap-0.5 rounded-md border border-input bg-muted/40 p-0.5">
      {opciones.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            "cursor-pointer rounded-sm px-2.5 py-1 text-xs font-medium transition-colors",
            vista === o.value ? "bg-card text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Calendario de pagos (P39) · EXCLUSIVO de Mesa de Control. Navega por MES;
 * dos vistas del mismo dataset: calendario mensual (carga por día de un
 * vistazo) y tabla (detalle completo + export). KPIs y semáforo con leyenda.
 *
 * Dos queries: la principal SIN `page` (el calendario mensual y los KPIs
 * necesitan todos los días del periodo) y una segunda PAGINADA en el server
 * (page/pageSize) exclusiva de la vista Tabla.
 */
export function CalendarioPagosPage() {
  const [mes, setMes] = useState(() => dayjs().startOf("month").format("YYYY-MM-DD"));
  const [organizacionId, setOrganizacionId] = useState("");
  const [moneda, setMoneda] = useState("");
  const [vista, setVista] = useState<"calendario" | "tabla">("calendario");
  const [diaSeleccionado, setDiaSeleccionado] = useState<string | null>(null);
  const [pageTabla, setPageTabla] = useState(1);
  const [reprogramando, setReprogramando] = useState<PagoProgramado | null>(null);

  // Sociedades visibles del usuario · mismo combo (recortado) del histórico.
  const { data: combos } = useFiltrosHistorico();

  const params = useMemo(
    () => ({
      desde: mes,
      hasta: dayjs(mes).endOf("month").format("YYYY-MM-DD"),
      organizacionId: organizacionId || undefined,
      moneda: moneda || undefined,
    }),
    [mes, organizacionId, moneda],
  );

  // Cambiar mes o filtros invalida la página de la tabla · de vuelta a la 1.
  useEffect(() => {
    setPageTabla(1);
  }, [params]);

  const { data, isPending, isError, error, refetch, isFetching } = useCalendarioPagos(params);
  // Query paginada de la vista Tabla · solo fetchea cuando la tabla es visible.
  const tabla = useQuery({
    ...calendarioPagosQueryOptions({ ...params, page: pageTabla, pageSize: PAGE_SIZE_TABLA }),
    enabled: vista === "tabla",
  });
  const pagos = data?.pagos ?? [];
  const kpis = data?.kpis;
  const monedasPresentes = [...new Set(pagos.map((p) => p.moneda ?? "—"))];
  const pagosDelDia = diaSeleccionado ? pagos.filter((p) => p.fechaPago === diaSeleccionado) : null;

  const cambiarMes = (delta: number) => {
    setMes(dayjs(mes).add(delta, "month").format("YYYY-MM-DD"));
    setDiaSeleccionado(null);
  };

  return (
    <div className="w-full px-4 py-6 md:px-6">
      <PageHeader
        title="Calendario de pagos"
        description="Pagos programados de las solicitudes autorizadas · la fecha la fija Mesa de Control al autorizar el último nivel."
        actions={
          <IconAction label="Refrescar" variant="default" size="lg" onClick={() => refetch()} loading={isFetching}>
            <IconRefresh size={16} />
          </IconAction>
        }
      />
      <Stack gap="md">
        {/* Navegación de mes + filtros + toggle de vista */}
        <Group justify="space-between" className="flex-wrap" gap="sm">
          <Group gap="xs">
            <IconAction label="Mes anterior" variant="default" onClick={() => cambiarMes(-1)}>
              <IconChevronLeft size={16} />
            </IconAction>
            <Heading order={4} className="min-w-42.5 text-center capitalize">
              {dayjs(mes).format("MMMM YYYY")}
            </Heading>
            <IconAction label="Mes siguiente" variant="default" onClick={() => cambiarMes(1)}>
              <IconChevronRight size={16} />
            </IconAction>
          </Group>
          <Group gap="sm">
            <Select
              options={[{ value: "", label: "Todas las sociedades" }, ...(combos?.sociedades ?? [])]}
              value={organizacionId}
              onValueChange={setOrganizacionId}
              searchable
              wrapperClassName="w-58"
            />
            <Select
              options={[{ value: "", label: "Moneda" }, ...monedasPresentes.map((m) => ({ value: m, label: m }))]}
              value={moneda}
              onValueChange={setMoneda}
              wrapperClassName="w-28"
            />
            <VistaToggle vista={vista} onChange={setVista} />
          </Group>
        </Group>

        {/* KPIs del periodo */}
        {kpis && (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <KpiCard
              titulo="Programado en el mes"
              valor={
                kpis.totalesPorMoneda.length === 0
                  ? "$0.00"
                  : kpis.totalesPorMoneda.map((t) => `$${FMT.format(t.total)} ${t.moneda}`).join(" · ")
              }
              detalle={`${kpis.totalPagos} pago${kpis.totalPagos === 1 ? "" : "s"}`}
            />
            <KpiCard titulo="Pagan hoy" valor={String(kpis.pagosHoy)} />
            <KpiCard titulo="Próximos 7 días" valor={String(kpis.pagosProximos7)} />
            <KpiCard titulo="Vencidos sin depositar" valor={String(kpis.pagosVencidos)} alerta={kpis.pagosVencidos > 0} />
          </div>
        )}

        {isPending ? (
          <Card padding="lg">
            <Stack gap="sm">
              <Skeleton height={36} />
              <Skeleton height={200} />
            </Stack>
          </Card>
        ) : isError ? (
          <Alert variant="error">Error: {(error as Error).message}</Alert>
        ) : vista === "calendario" ? (
          // Calendario a la izquierda · detalle del día seleccionado a la
          // derecha (cards compactas · la tabla completa vive en vista Tabla).
          <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <CalendarioMensual mes={mes} pagos={pagos} diaSeleccionado={diaSeleccionado} onSeleccionarDia={setDiaSeleccionado} />
            </div>
            <div className="lg:col-span-4">
              <PagosDelDiaPanel dia={diaSeleccionado} pagos={pagosDelDia ?? []} onReprogramar={setReprogramando} />
            </div>
          </div>
        ) : (
          <PagosTable
            pagos={tabla.data?.pagos ?? []}
            pagosExport={pagos}
            total={tabla.data?.total ?? 0}
            page={pageTabla}
            pageSize={PAGE_SIZE_TABLA}
            onPageChange={setPageTabla}
            onReprogramar={setReprogramando}
          />
        )}

        <SemaforoLeyenda />
      </Stack>

      <ReprogramarFechaModal pago={reprogramando} onClose={() => setReprogramando(null)} />
    </div>
  );
}

function KpiCard({
  titulo,
  valor,
  detalle,
  alerta = false,
}: {
  titulo: string;
  valor: string;
  detalle?: string;
  alerta?: boolean;
}) {
  return (
    <Card padding="md">
      <Text size="xs" c="dimmed" className="uppercase" fw={600}>
        {titulo}
      </Text>
      <Text size="xl" fw={700} c={alerta ? "peligro-600" : undefined} className="mt-1">
        {valor}
      </Text>
      {detalle && (
        <Text size="xs" c="dimmed">
          {detalle}
        </Text>
      )}
    </Card>
  );
}
